import Papa from 'papaparse'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createImportPreview,
  updateImportPreview,
  useGetImportPreview,
  performSetCompanies,
  performSetTags,
  performSetDeals,
  validateDealAccountOwners,
  validateDealSalesStages,
  performSetContacts,
  performSetCompanyNotes,
  usePollStatus,
  setUnmatchedChains,
} from './importer-api'
import {
  ColumnType,
  ColumnColors,
  ColumnOption,
  CSVRow,
  ImportData,
  CSVRowState,
  ImportDataColumnar,
  InternalColumns,
} from './importer-types'
import { ChainSearchResult, DoorSearchResult } from 'features/elastic/searchers'
import { useNavigate } from 'react-router-dom'
import apiService from 'services/api'
import { toast } from 'react-toastify'
import {
  FB_REMOVE_CELL_VALUE,
  ValidateModalData,
  ValidateModalRow,
} from './validate-modal'
import { ColumnFilter, RowSelectionState } from '@tanstack/react-table'
import { ExportModalColumn } from 'components/Modals/ExportModal/ExportModal'
import { CompanyTypeSlug } from 'models/companies'

export type CanImportResult = [
  canImport: boolean,
  reasons: string[],
  counts: { canBeImported: number; imported: number },
]

export const useImporterViewModel = (importNumber: number | null) => {
  const navigate = useNavigate()
  const api = apiService()

  const { data: companyCategories } = api.useGetCompanyCategories()
  const { data: salesStages } = api.useGetSalesStages()
  const { data: users } = api.useGetUsers()
  const { data: products } = api.useGetProducts()
  const {
    data: importPreview,
    isPending: isLoading,
    isFetching,
    refetch,
  } = useGetImportPreview(importNumber)
  const {
    data: importPreviewStatus,
    isPending: isLoadingStatus,
    refetch: refetchStatus,
  } = usePollStatus(importNumber)

  const [showSelectDealProductModal, setShowSelectDealProductModal] =
    useState(false)
  const [selectedDealProduct, setSelectedDealProduct] = useState<string | null>(
    null
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSettingCompanies, setIsSettingCompanies] = useState(false)
  const [isSettingTags, setIsSettingTags] = useState(false)
  const [isSettingDeals, setIsSettingDeals] = useState(false)
  const [isSettingContacts, setIsSettingContacts] = useState(false)
  const [isSettingCompanyNotes, setIsSettingCompanyNotes] = useState(false)
  const [isSettingUnmatchedChains, setIsSettingUnmatchedChains] =
    useState(false)
  const [importData, setImportData] = useState<ImportData | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [hasPendingChanges, setHasPendingChanges] = useState(false)
  const [selectedTab, setSelectedTab] = useState<CSVRowState>('pending_review')
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [validateModalData, setValidateModalData] =
    useState<ValidateModalData | null>(null)
  const [columnFiltering, setColumnFiltering] = useState<ColumnFilter[]>([])
  const [showHeaderMappingModal, setShowHeaderMappingModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showConfigureExportModal, setShowConfigureExportModal] =
    useState(false)
  const [reviewing, setReviewing] = useState(true)

  type ExportConfig = {
    allowSuggestions: boolean
    suggestionAmount: number
  }
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    allowSuggestions: true,
    suggestionAmount: 1,
  })

  /* -------------
  Computed Values
  ------------- */
  const tableData = useMemo<Record<CSVRowState, CSVRow[]>>(() => {
    if (!importData)
      return {
        pending_review: [],
        reviewed: [],
      }

    const reviewed = importData.rows.filter(
      (r) =>
        r[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
        r[InternalColumns.IS_CHAIN_MERGED]
    )

    const pending_review = importData.rows.filter(
      (r) =>
        r[InternalColumns.SET_CHAIN_PROXY_ID] == -1 &&
        !r[InternalColumns.IS_CHAIN_MERGED]
    )

    return {
      pending_review: pending_review,
      reviewed: reviewed,
    }
  }, [importData])

  const areAllRowsSet = useMemo(() => {
    if (!importData) return false

    const set = importData.rows.filter(
      (r) =>
        r[InternalColumns.SET_CHAIN_PROXY_ID] !== -1 ||
        r[InternalColumns.IS_CHAIN_MERGED]
    )
    const ready = importData.rows.filter((r) => r[InternalColumns.READY])

    return set.length === ready.length && set.length > 0
  }, [importData])

  const exportColumns = useMemo<ExportModalColumn[]>(() => {
    if (!importData) return []

    function toTitle(key: string) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase())
    }

    const baseColumns: ExportModalColumn['elements'] = []
    importData.headers.forEach((h) => {
      baseColumns.push([h.mapped, h.original])
    })

    const suggestionColumns: ExportModalColumn['elements'] = []
    chainSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__chain__${key}`,
        `Chain ${toTitle(key)}`,
      ])
    })
    doorSuggestionKeys.forEach((key) => {
      suggestionColumns.push([
        `__suggestion__door__${key}`,
        `Door ${toTitle(key)}`,
      ])
    })

    const generatedColumns: ExportModalColumn['elements'] = []
    generatedColumnKeys.forEach((key) => {
      generatedColumns.push([
        key,
        `${toTitle(key.replace('__generated__', ''))}`,
      ])
    })

    const groups = [
      {
        title: 'Uploaded Data',
        elements: baseColumns,
      },
      {
        title: 'Matched Data',
        elements: generatedColumns,
      },
    ]

    if (exportConfig.allowSuggestions) {
      groups.push({
        title: 'Suggested Data',
        elements: suggestionColumns,
      })
    }

    return groups
  }, [importData, exportConfig])

  const canAddTags = useMemo<CanImportResult>(() => {
    if (!importData) {
      return [
        false,
        ['No data'],
        {
          canBeImported: 0,
          imported: 0,
        },
      ]
    }
    const tagHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'tag_data'
    )
    const hasHeader = Boolean(tagHeader)

    let amountTags = 0
    let amountImported = 0
    for (let i = 0; i < (importData.rows.length ?? 0); i++) {
      const r = importData.rows[i]
      const tagString = r[tagHeader?.original as string] ?? ''
      const tagsSet = r[InternalColumns.TAG_SET] !== -1
      if (tagString !== '') {
        const tags = tagString.split(',')
        amountTags += tags.length
        if (tagsSet) {
          amountImported += tags.length
        }
      }
    }

    const hasPendingTags = amountTags > amountImported

    const ableTo: boolean = hasHeader && hasPendingTags
    const reasonsUnableTo: string[] = []
    if (ableTo) {
      return [
        ableTo,
        reasonsUnableTo,
        {
          canBeImported: hasHeader ? amountTags : 0,
          imported: amountImported,
        },
      ]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        `You need to assign a column to the "${columnOptions['tag_name'].name}" field.`
      )
    }
    if (!hasPendingTags) {
      reasonsUnableTo.push('Tags have already been imported for all rows.')
    }

    return [
      ableTo,
      reasonsUnableTo,
      {
        canBeImported: hasHeader ? amountTags : 0,
        imported: amountImported,
      },
    ]
  }, [importData])

  const canAddDeals = useMemo<CanImportResult>(() => {
    const dealHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'deal_data'
    )
    const hasHeader = Boolean(dealHeader)

    const readyCount =
      importData?.rows.filter((r) => r[InternalColumns.DEAL_SET] === -1)
        .length ?? 0

    const importedCount =
      importData?.rows.filter((r) => r[InternalColumns.DEAL_SET] !== -1)
        .length ?? 0

    const lenRowsWithDealValues =
      importData?.rows.filter((r) => r[dealHeader?.original as string] !== '')
        .length ?? 0

    const hasPendingDeals = readyCount >= lenRowsWithDealValues

    const ableTo: boolean = hasHeader && hasPendingDeals
    const reasonsUnableTo: string[] = []
    if (ableTo) {
      return [
        ableTo,
        reasonsUnableTo,
        {
          canBeImported: hasHeader ? lenRowsWithDealValues : 0,
          imported: importedCount,
        },
      ]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        'You need to assign at least one column to a Deal field'
      )
    }
    if (!hasPendingDeals) {
      reasonsUnableTo.push('Deals have already been imported for all rows.')
    }

    return [
      ableTo,
      reasonsUnableTo,
      {
        canBeImported: hasHeader ? lenRowsWithDealValues : 0,
        imported: importedCount,
      },
    ]
  }, [importData])

  const canAddContacts = useMemo<CanImportResult>(() => {
    const contactHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'contact_data'
    )
    const hasHeader = Boolean(contactHeader)

    const readyCount =
      importData?.rows.filter((r) => r[InternalColumns.CONTACT_SET] === -1)
        .length ?? 0

    const importedCount =
      importData?.rows.filter((r) => r[InternalColumns.CONTACT_SET] !== -1)
        .length ?? 0

    const lenRowsWithContactValues =
      importData?.rows.filter(
        (r) => r[contactHeader?.original as string] !== ''
      ).length ?? 0

    const hasPendingContacts = readyCount >= lenRowsWithContactValues

    const ableTo = hasHeader && hasPendingContacts
    const reasonsUnableTo: string[] = []

    if (ableTo) {
      return [
        ableTo,
        reasonsUnableTo,
        {
          canBeImported: hasHeader ? lenRowsWithContactValues : 0,
          imported: importedCount,
        },
      ]
    }

    if (!hasHeader) {
      reasonsUnableTo.push('You need to assign a column to a Contact field.')
    }

    if (!hasPendingContacts) {
      reasonsUnableTo.push('Contacts have already been imported for all rows.')
    }

    return [
      ableTo,
      reasonsUnableTo,
      {
        canBeImported: hasHeader ? lenRowsWithContactValues : 0,
        imported: importedCount,
      },
    ]
  }, [importData])

  const canAddCompanyNotes = useMemo<CanImportResult>(() => {
    if (!importData) {
      return [
        false,
        ['No data'],
        {
          canBeImported: 0,
          imported: 0,
        },
      ]
    }
    const companyNotesHeader = importData?.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'company_notes'
    )
    const hasHeader = Boolean(companyNotesHeader)

    let amountNotes = 0
    let amountImported = 0
    for (let i = 0; i < (importData.rows.length ?? 0); i++) {
      const r = importData.rows[i]
      const noteString = r[companyNotesHeader?.original as string] ?? ''
      const notesSet = r[InternalColumns.COMPANY_NOTES_SET] !== -1
      if (noteString !== '') {
        const notes = noteString.split(',')
        amountNotes += notes.length
        if (notesSet) {
          amountImported += notes.length
        }
      }
    }

    const hasPendingNotes = amountNotes > amountImported

    const ableTo: boolean = hasHeader && hasPendingNotes
    const reasonsUnableTo: string[] = []
    if (ableTo) {
      return [
        ableTo,
        reasonsUnableTo,
        {
          canBeImported: hasHeader ? amountNotes : 0,
          imported: amountImported,
        },
      ]
    }

    if (!hasHeader) {
      reasonsUnableTo.push(
        `You need to assign a column to the "${columnOptions['company_notes'].name}" field.`
      )
    }
    if (!hasPendingNotes) {
      reasonsUnableTo.push(
        'Company Notes have already been imported for all rows.'
      )
    }

    return [
      ableTo,
      reasonsUnableTo,
      {
        canBeImported: hasHeader ? amountNotes : 0,
        imported: amountImported,
      },
    ]
  }, [importData])

  const companySubcategories = useMemo(() => {
    return companyCategories?.reduce(
      (acc, c) => {
        c.sub_categories?.forEach((s) => {
          acc.push({
            label: (
              <p className="text-sm">
                {c.name} - {s.name}
              </p>
            ),
            value: s.id,
            category_id: c.id,
            slug: s.slug,
          })
        })
        return acc
      },
      [] as {
        label: any
        value: number
        category_id: number
        slug: CompanyTypeSlug
      }[]
    )
  }, [companyCategories])

  const chainMergerAvailableSubcategories = useMemo<
    Record<number, string>
  >(() => {
    const uniqueSubcategories = new Set(
      importData?.rows.map(
        (r) => r[InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID]
      )
    )
    const subCats =
      companySubcategories?.filter((s) => uniqueSubcategories.has(s.value)) ??
      []
    const subCatMap: Record<number, string> = {}
    for (const s of subCats) {
      subCatMap[s.value] = s.label
    }
    return subCatMap
  }, [importData, companySubcategories])

  const mappedColumns = useMemo(() => {
    return importData?.headers.map((h) => h.mapped)
  }, [importData])

  const shouldShowBulkActions = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r]).length > 0
  }, [rowSelection])

  const selectedRowIds = useMemo(() => {
    return Object.keys(rowSelection).filter((r) => rowSelection[r])
  }, [rowSelection])

  const selectedSubcategoryFilter = useMemo(() => {
    return columnFiltering.find(
      (f) => f.id === InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID
    )?.value
  }, [columnFiltering])

  const wizardHeaders = useMemo(() => {
    const map = {
      name: '',
      domain: '',
      address: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      full_address: '',
    }

    importData?.headers.forEach((header) => {
      switch (header.mapped) {
        case 'company_name':
          map.name = header.original
          break
        case 'company_domain':
          map.domain = header.original
          break
        case 'company_address_street':
          map.street = header.original
          break
        case 'company_address_city':
          map.city = header.original
          break
        case 'company_address_state':
          map.state = header.original
          break
        case 'company_address_zip':
          map.zip = header.original
          break
        case 'company_full_address':
          map.full_address = header.original
          break
      }
    })

    return map
  }, [importData?.headers])

  const otherOtherType = useMemo(() => {
    return companySubcategories?.find((subcat) => subcat.slug === 'other-other')
  }, [companySubcategories])

  // Companies Groups - Rows after deduplication by company
  const companiesPrematched = useMemo<CSVRow[]>(() => {
    const uniqueHashes = new Set<string>()
    return (
      importData?.rows.reduce((acc, row) => {
        if (row[InternalColumns.READY]) {
          if (!uniqueHashes.has(row[InternalColumns.COMPANY_HASH])) {
            uniqueHashes.add(row[InternalColumns.COMPANY_HASH])
            acc.push(row)
          }
        }
        return acc
      }, [] as CSVRow[]) ?? []
    )
  }, [importData])

  const amountCompaniesPrematched = useMemo(() => {
    return companiesPrematched.length
  }, [companiesPrematched])

  const companiesPendingReview = useMemo<CSVRow[]>(() => {
    const uniqueHashes = new Set<string>()
    return (
      importData?.rows.reduce((acc, row) => {
        if (!row[InternalColumns.READY]) {
          if (!uniqueHashes.has(row[InternalColumns.COMPANY_HASH])) {
            uniqueHashes.add(row[InternalColumns.COMPANY_HASH])
            acc.push(row)
          }
        }
        return acc
      }, [] as CSVRow[]) ?? []
    )
  }, [importData])

  const amountCompaniesPendingReview = useMemo(() => {
    return companiesPendingReview.length
  }, [companiesPendingReview])

  const companiesLeftUnmatched = useMemo<CSVRow[]>(() => {
    const uniqueHashes = new Set<string>()
    return (
      importData?.rows.reduce((acc, row) => {
        if (
          !row[InternalColumns.READY] &&
          !row[InternalColumns.MATCHED_CHAIN] &&
          !row[InternalColumns.MATCHED_DOOR]
        ) {
          if (!uniqueHashes.has(row[InternalColumns.COMPANY_HASH])) {
            uniqueHashes.add(row[InternalColumns.COMPANY_HASH])
            acc.push(row)
          }
        }
        return acc
      }, [] as CSVRow[]) ?? []
    )
  }, [importData])

  const amountCompaniesLeftUnmatched = useMemo(() => {
    return companiesLeftUnmatched.length
  }, [companiesLeftUnmatched])

  const allCompanies = useMemo<CSVRow[]>(() => {
    const uniqueHashes = new Set<string>()
    return (
      importData?.rows.reduce((acc, row) => {
        if (!uniqueHashes.has(row[InternalColumns.COMPANY_HASH])) {
          uniqueHashes.add(row[InternalColumns.COMPANY_HASH])
          acc.push(row)
        }
        return acc
      }, [] as CSVRow[]) ?? []
    )
  }, [importData])

  // Import Summary Data for Summary Table
  const importSummaryData = useMemo(() => {
    if (!importData) return []

    // Group rows by company hash
    const companyGroups = new Map<string, CSVRow[]>()

    importData.rows.forEach((row) => {
      const hash = row[InternalColumns.COMPANY_HASH]
      if (!companyGroups.has(hash)) {
        companyGroups.set(hash, [])
      }
      companyGroups.get(hash)!.push(row)
    })

    // Get mapped headers for each data type
    const dealHeaders = importData.headers.filter(
      (h) => columnOptions[h.mapped]?.type === 'deal_data'
    )
    const tagHeader = importData.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'tag_data'
    )
    const contactHeaders = importData.headers.filter(
      (h) => columnOptions[h.mapped]?.type === 'contact_data'
    )
    const notesHeader = importData.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'company_notes'
    )

    // Build preview data for each company group
    const summaryData = Array.from(companyGroups.entries()).map(
      ([companyHash, rows]) => {
        const firstRow = rows[0]

        // Company name
        const companyName = firstRow[InternalColumns.IS_CUSTOM_COMPANY]
          ? 'User-Generated'
          : firstRow[InternalColumns.MATCHED_CHAIN]?.name || 'Excluded'

        // Collect deals from all rows in this group
        const deals = rows
          .map((row) => {
            const dealData: Record<string, any> = {}
            let hasData = false

            dealHeaders.forEach((header) => {
              const value = row[header.original]
              if (value !== undefined && value !== null && value !== '') {
                dealData[header.mapped] = value
                hasData = true
              }
            })

            // Also check for deal product from internal field
            if (row[InternalColumns.DEAL_PRODUCT]) {
              dealData['deal_product'] = row[InternalColumns.DEAL_PRODUCT]
              hasData = true
            }

            return hasData ? dealData : null
          })
          .filter((d) => d !== null)

        // Collect and deduplicate tags from all rows
        const tagsSet = new Set<string>()
        if (tagHeader) {
          rows.forEach((row) => {
            const tagValue = row[tagHeader.original]
            if (tagValue) {
              // Split by comma and trim
              const tags = String(tagValue)
                .split(',')
                .map((t) => t.trim())
                .filter((t) => t.length > 0)
              tags.forEach((tag) => tagsSet.add(tag))
            }
          })
        }
        const tags = Array.from(tagsSet)

        // Collect and deduplicate notes from all rows
        const notesSet = new Set<string>()
        if (notesHeader) {
          rows.forEach((row) => {
            const noteValue = row[notesHeader.original]
            if (noteValue) {
              // Split by comma and trim
              const notes = String(noteValue)
                .split(',')
                .map((n) => n.trim())
                .filter((n) => n.length > 0)
              notes.forEach((note) => notesSet.add(note))
            }
          })
        }
        const notes = Array.from(notesSet)

        // Collect contacts from all rows in this group
        const contacts = rows
          .map((row) => {
            const contactData: Record<string, any> = {}
            let hasData = false

            contactHeaders.forEach((header) => {
              const value = row[header.original]
              if (value !== undefined && value !== null && value !== '') {
                contactData[header.mapped] = value
                hasData = true
              }
            })

            return hasData ? contactData : null
          })
          .filter((c) => c !== null)

        // Build company link
        const isCustomCompany = firstRow[InternalColumns.IS_CUSTOM_COMPANY]
        const setChainProxyId = firstRow[InternalColumns.SET_CHAIN_PROXY_ID]
        const matchedChain = firstRow[InternalColumns.MATCHED_CHAIN]

        let firstbiteId = ''
        if (isCustomCompany) {
          firstbiteId = 'UG' + setChainProxyId
        } else if (matchedChain) {
          firstbiteId = (matchedChain.type ?? '') + (matchedChain.item_id ?? '')
        }

        return {
          companyHash,
          companyName,
          isCustomCompany,
          firstbiteId,
          deals,
          tags,
          notes,
          contacts,
        }
      }
    )

    console.log('Import Summary Data:', summaryData)
    return summaryData
  }, [importData])

  // Detailed Import Data - separate arrays for each import type
  const detailedImportData = useMemo(() => {
    if (!importData) {
      return {
        deals: [],
        tags: [],
        contacts: [],
        notes: [],
        ugcs: [],
      }
    }

    // Group rows by company hash
    const companyGroups = new Map<string, CSVRow[]>()
    importData.rows.forEach((row) => {
      const hash = row[InternalColumns.COMPANY_HASH]
      if (!companyGroups.has(hash)) {
        companyGroups.set(hash, [])
      }
      companyGroups.get(hash)!.push(row)
    })

    // Get mapped headers for each data type
    const dealHeaders = importData.headers.filter(
      (h) => columnOptions[h.mapped]?.type === 'deal_data'
    )
    const tagHeader = importData.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'tag_data'
    )
    const contactHeaders = importData.headers.filter(
      (h) => columnOptions[h.mapped]?.type === 'contact_data'
    )
    const notesHeader = importData.headers.find(
      (h) => columnOptions[h.mapped]?.type === 'company_notes'
    )

    const deals: any[] = []
    const tags: any[] = []
    const contacts: any[] = []
    const notes: any[] = []
    const ugcs: any[] = []

    const hasDealHeaders = dealHeaders.length > 0
    const hasContactHeaders = contactHeaders.length > 0

    // Process each company group
    Array.from(companyGroups.entries()).forEach(([companyHash, rows]) => {
      const firstRow = rows[0]

      // Company info
      const isCustomCompany = firstRow[InternalColumns.IS_CUSTOM_COMPANY]
      const setChainProxyId = firstRow[InternalColumns.SET_CHAIN_PROXY_ID]
      const matchedChain = firstRow[InternalColumns.MATCHED_CHAIN]
      const companyName = isCustomCompany
        ? 'User-Generated'
        : matchedChain?.name || 'Excluded'

      let firstbiteId = ''
      if (isCustomCompany) {
        firstbiteId = 'UG' + setChainProxyId
      } else if (matchedChain) {
        firstbiteId = (matchedChain.type ?? '') + (matchedChain.item_id ?? '')
      }

      const companyInfo = {
        companyHash,
        companyName,
        isCustomCompany,
        firstbiteId,
        isImported: setChainProxyId !== -1,
      }

      // UGCs
      if (isCustomCompany) {
        ugcs.push(companyInfo)
      }

      // Deals
      if (hasDealHeaders) {
        rows.forEach((row) => {
          const dealData: Record<string, any> = {}
          let hasData = false

          dealHeaders.forEach((header) => {
            const value = row[header.original]
            if (value !== undefined && value !== null && value !== '') {
              dealData[header.mapped] = value
              hasData = true
            }
          })

          if (row[InternalColumns.DEAL_PRODUCT]) {
            dealData['deal_product'] = row[InternalColumns.DEAL_PRODUCT]
            hasData = true
          }

          if (hasData) {
            deals.push({
              ...companyInfo,
              ...dealData,
              isImported: row[InternalColumns.DEAL_SET] !== -1,
            })
          }
        })
      }

      // Tags
      if (tagHeader) {
        rows.forEach((row) => {
          const tagValue = row[tagHeader.original]
          if (tagValue) {
            const tagList = String(tagValue)
              .split(',')
              .map((t) => t.trim())
              .filter((t) => t.length > 0)

            tagList.forEach((tag) => {
              tags.push({
                ...companyInfo,
                tag_name: tag,
                isImported: row[InternalColumns.TAG_SET] !== -1,
              })
            })
          }
        })
      }

      // Contacts
      if (hasContactHeaders) {
        rows.forEach((row) => {
          const contactData: Record<string, any> = {}
          let hasData = false

          contactHeaders.forEach((header) => {
            const value = row[header.original]
            if (value !== undefined && value !== null && value !== '') {
              contactData[header.mapped] = value
              hasData = true
            }
          })

          if (hasData) {
            contacts.push({
              ...companyInfo,
              ...contactData,
              isImported: row[InternalColumns.CONTACT_SET] !== -1,
            })
          }
        })
      }

      // Notes
      if (notesHeader) {
        rows.forEach((row) => {
          const noteValue = row[notesHeader.original]
          if (noteValue) {
            const noteList = String(noteValue)
              .split(',')
              .map((n) => n.trim())
              .filter((n) => n.length > 0)

            noteList.forEach((note) => {
              notes.push({
                ...companyInfo,
                company_note: note,
                isImported: row[InternalColumns.COMPANY_NOTES_SET] !== -1,
              })
            })
          }
        })
      }
    })

    return { deals, tags, contacts, notes, ugcs }
  }, [importData])

  // Unified counts for import data categories
  const importCounts = useMemo(() => {
    const data = detailedImportData

    return {
      ugcs: {
        total: data.ugcs.length,
        imported: data.ugcs.filter((u) => u.isImported).length,
      },
      tags: {
        total: data.tags.length,
        imported: data.tags.filter((t) => t.isImported).length,
      },
      deals: {
        total: data.deals.length,
        imported: data.deals.filter((d) => d.isImported).length,
      },
      contacts: {
        total: data.contacts.length,
        imported: data.contacts.filter((c) => c.isImported).length,
      },
      notes: {
        total: data.notes.length,
        imported: data.notes.filter((n) => n.isImported).length,
      },
    }
  }, [detailedImportData])

  const isAllImported = useMemo(() => {
    const { ugcs, tags, deals, contacts, notes } = importCounts
    const hasData =
      ugcs.total > 0 ||
      tags.total > 0 ||
      deals.total > 0 ||
      contacts.total > 0 ||
      notes.total > 0

    if (!hasData) return true

    return (
      ugcs.imported === ugcs.total &&
      tags.imported === tags.total &&
      deals.imported === deals.total &&
      contacts.imported === contacts.total &&
      notes.imported === notes.total
    )
  }, [importCounts])

  /* -------------
  Effects
  ------------- */
  useEffect(() => {
    if (importPreview) {
      loadImportPreviewData(importPreview.data)
    }
    return () => {
      setImportData(null)
    }
  }, [importPreview])

  useEffect(() => {
    if (importPreviewStatus?.status === 'idle') {
      refetch()
    }
  }, [importPreviewStatus])

  /* -------------
  Field Handlers
  ------------- */
  function updateMap(original_header: string, mapped_column: string) {
    if (!importData) return
    const data = [...importData.headers]
    const header = data.find((h) => h.original === original_header)
    if (header) {
      header.mapped = mapped_column
    }
    setImportData({ ...importData, headers: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoor(
    row_id: string,
    matched_door: CSVRow[typeof InternalColumns.MATCHED_DOOR],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r[InternalColumns.ROW_ID] === row_id)
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_DOOR] = matched_door
      data[row][InternalColumns.IS_DOOR_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedDoorFromCompanyHash(
    company_hash: string,
    matched_door: CSVRow[typeof InternalColumns.MATCHED_DOOR],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex(
      (r) => r[InternalColumns.COMPANY_HASH] === company_hash
    )
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_DOOR] = matched_door
      data[row][InternalColumns.IS_DOOR_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChain(
    row_id: string,
    matched_chain: CSVRow[typeof InternalColumns.MATCHED_CHAIN],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex((r) => r[InternalColumns.ROW_ID] === row_id)
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_CHAIN] = matched_chain
      data[row][InternalColumns.IS_CHAIN_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateMatchedChainFromCompanyHash(
    company_hash: string,
    matched_chain: CSVRow[typeof InternalColumns.MATCHED_CHAIN],
    manual_match: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    const row = data.findIndex(
      (r) => r[InternalColumns.COMPANY_HASH] === company_hash
    )
    if (row !== -1) {
      data[row][InternalColumns.MATCHED_CHAIN] = matched_chain
      data[row][InternalColumns.IS_CHAIN_MANUAL_MATCH] = manual_match
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompany(
    row_ids: string[],
    is_custom_company: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.IS_CUSTOM_COMPANY] = is_custom_company
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateIsCustomCompanyFromCompanyHash(
    company_hashes: string[],
    is_custom_company: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.IS_CUSTOM_COMPANY] = is_custom_company
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategory(
    row_ids: string[],
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID] = category_id
        d[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID] = subcategory_id
        d[InternalColumns.IS_CUSTOM_COMPANY] = true
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateCustomCompanyCategoryFromCompanyHash(
    company_hashes: string[],
    category_id: number,
    subcategory_id: number | null
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.CUSTOM_COMPANY_CATEGORY_ID] = category_id
        d[InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID] = subcategory_id
        d[InternalColumns.IS_CUSTOM_COMPANY] = true
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateRowsReady(row_ids: string[], ready: boolean) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (row_ids.includes(d[InternalColumns.ROW_ID])) {
        d[InternalColumns.READY] = ready
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  function updateRowsReadyFromCompanyHash(
    company_hashes: string[],
    ready: boolean
  ) {
    if (!importData) return
    const data = [...importData.rows]
    for (const d of data) {
      if (company_hashes.includes(d[InternalColumns.COMPANY_HASH])) {
        d[InternalColumns.READY] = ready
      }
    }
    setImportData({ ...importData, rows: data })
    setHasPendingChanges(true)
  }

  const handleManualSearchSelect = (
    door: DoorSearchResult | null,
    chain: ChainSearchResult | null
  ) => {
    if (selectedRowId === null || !importData) return

    if (door) {
      updateMatchedDoor(selectedRowId, door, true)
      updateMatchedChain(selectedRowId, chain, true)
    } else if (chain) {
      updateMatchedChain(selectedRowId, chain, true)
    }

    setSelectedRowId(null)
  }

  /* -------------
  Actions
  ------------- */
  async function handleSave(localImportData?: ImportData) {
    setIsSaving(true)

    let dataToSave: ImportData | null = null
    if (localImportData) {
      dataToSave = { ...localImportData }
    } else if (importData) {
      dataToSave = { ...importData }
    }

    if (!dataToSave) {
      return
    }

    const data = toColumnarJSON(dataToSave)

    try {
      // Update
      if (importNumber) {
        await updateImportPreview(importNumber, {
          import_data: { data },
        })
      } else {
        // Create
        const rawDataString = await rawFile?.text()
        const fileName = rawFile?.name
        const response = await createImportPreview({
          import_data: {
            data,
            raw_data: rawDataString ?? '',
            name: fileName ?? '',
          },
        })
        navigate(`/importer/${response.import_number}`, { replace: true })
        return response.import_number
      }
      setHasPendingChanges(false)
      return importNumber
    } catch {
      toast.error('Error saving import preview')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleReview() {
    setReviewing(true)
  }

  async function handleSetCompanies() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingCompanies(true)
    try {
      await handleSave()
      const data = await performSetCompanies(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingCompanies(false)
    }
  }

  async function handleSetTags() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingTags(true)
    try {
      const data = await performSetTags(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingTags(false)
    }
  }

  async function handleSetDeals(): Promise<boolean> {
    if (!importData) return false
    if (!importNumber) return false

    setIsSettingDeals(true)
    try {
      // Validate Deal Account Owners
      if (importData.headers.some((h) => h.mapped === 'deal_account_owner')) {
        const data = await validateDealAccountOwners(importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Account Owners',
            missing: data.missing,
            found: data.found,
            options:
              users?.map((u) => ({
                label: u.email,
                value: u.email,
              })) ?? [],
            itemName: 'Deal Account Owner',
            columnName: 'deal_account_owner',
          })
          return false
        }
      }

      // Validate Deal Sales Stages
      if (importData.headers.some((h) => h.mapped === 'deal_sales_stage')) {
        const data = await validateDealSalesStages(importNumber)
        if (!data.success) {
          setValidateModalData({
            title: 'Invalid Deal Sales Stages',
            missing: data.missing,
            found: data.found,
            options:
              salesStages?.map((s) => ({
                label: s.stage,
                value: s.stage,
              })) ?? [],
            itemName: 'Deal Sales Stage',
            columnName: 'deal_sales_stage',
          })
          return false
        }
      }

      // Validate Deal Product
      if (!importData.headers.some((h) => h.mapped === 'deal_product')) {
        setShowSelectDealProductModal(true)
        return false
      }

      const data = await performSetDeals(importNumber)
      loadImportPreviewData(data.data)
      return true
    } catch (error) {
      console.error(error)
      return false
    } finally {
      setIsSettingDeals(false)
    }
  }

  async function handleSetContacts() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingContacts(true)
    try {
      const data = await performSetContacts(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingContacts(false)
    }
  }

  async function handleSetCompanyNotes() {
    if (!importData) return
    if (!importNumber) return
    setIsSettingCompanyNotes(true)
    try {
      const data = await performSetCompanyNotes(importNumber)
      loadImportPreviewData(data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingCompanyNotes(false)
    }
  }

  function handleValidation(changes: ValidateModalRow[], columnName: string) {
    if (!importData) return
    const mutData = { ...importData }
    const header = mutData.headers.find((h) => h.mapped === columnName)
    if (!header) return

    for (const change of changes) {
      const changeValue =
        change.mapped === FB_REMOVE_CELL_VALUE ? null : change.mapped

      for (let i = 0; i < mutData.rows.length; i++) {
        const row = mutData.rows[i]
        if (row[header.original] === change.original) {
          mutData.rows[i][header.original] = changeValue
        }
      }
    }

    setImportData(mutData)
    setValidateModalData(null)
  }

  function handleSelectDealProduct(productName: string) {
    if (!importData) return
    const mutData = { ...importData }

    mutData.rows.forEach((row) => {
      row[InternalColumns.DEAL_PRODUCT] = productName
    })

    mutData.headers.push({
      original: InternalColumns.DEAL_PRODUCT,
      mapped: 'deal_product',
      label: 'Deal Product',
    })
    setImportData(mutData)

    setShowSelectDealProductModal(false)
  }

  function handleConfigureExport() {
    setShowConfigureExportModal(false)
    setShowExportModal(true)
  }

  const handleExport = useCallback(
    async (selectedColumns: string[]) => {
      if (!importData) return

      const data = importData.rows

      // Transform rows to include exploded matched fields
      const transformedRows: Record<string, any>[] = []
      for (const row of data) {
        const baseRow: Record<string, any> = {}

        function addColumn(key: string, value: any, row: Record<string, any>) {
          if (selectedColumns.includes(key)) {
            row[key] = value
          }
        }

        // Remove nested objects
        for (const key in row) {
          if (
            [
              InternalColumns.ROW_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_ID,
              InternalColumns.EXISTING_CHAIN_PROXY_TYPE,
              InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
              InternalColumns.SET_CHAIN_PROXY_ID,
              InternalColumns.MATCHED_DOOR,
              InternalColumns.MATCHED_CHAIN,
              InternalColumns.SUGGESTED_DOORS,
              InternalColumns.SUGGESTED_CHAINS,
              InternalColumns.IS_CUSTOM_COMPANY,
              InternalColumns.CUSTOM_COMPANY_CATEGORY_ID,
              InternalColumns.CUSTOM_COMPANY_SUB_CATEGORY_ID,
              InternalColumns.READY,
              InternalColumns.IS_CHAIN_MERGED,
              InternalColumns.IS_DOOR_MANUAL_MATCH,
              InternalColumns.IS_CHAIN_MANUAL_MATCH,
              InternalColumns.TAG_SET,
              InternalColumns.DEAL_SET,
              InternalColumns.CONTACT_SET,
              InternalColumns.COMPANY_NOTES_SET,
              InternalColumns.DEAL_PRODUCT,
              InternalColumns.MATCHED_CHAIN_PK,
              InternalColumns.MATCHED_CHAIN_TYPE,
              InternalColumns.COMPANY_HASH,
            ].includes(key as InternalColumns) ||
            key.startsWith('matching__')
          ) {
            continue
          }
          baseRow[key] = row[key]
        }

        // ### SUGGESTIONS COLUMNS ###

        // Add blank columns for completeness
        chainSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__chain__${key}`, '', baseRow)
        })
        doorSuggestionKeys.forEach((key) => {
          addColumn(`__suggestion__door__${key}`, '', baseRow)
        })

        if (
          // Show only if row is not set - i.e. not imported
          row[InternalColumns.SET_CHAIN_PROXY_ID] === -1 &&
          exportConfig.allowSuggestions
        ) {
          // Add the first suggestions to base row
          chainSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__chain__${key}`,
              row[InternalColumns.SUGGESTED_CHAINS]?.[0]?.[key] ?? '',
              baseRow
            )
          })
          doorSuggestionKeys.forEach((key) => {
            addColumn(
              `__suggestion__door__${key}`,
              row[InternalColumns.SUGGESTED_DOORS]?.[0]?.[key] ?? '',
              baseRow
            )
          })

          // Add extra suggestions as new rows
          const extraSuggestionRows = []
          if (
            row[InternalColumns.SUGGESTED_CHAINS]?.length &&
            row[InternalColumns.SUGGESTED_CHAINS].length > 1
          ) {
            for (const s of row[InternalColumns.SUGGESTED_CHAINS].slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              chainSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__chain__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (
            row[InternalColumns.SUGGESTED_DOORS]?.length &&
            row[InternalColumns.SUGGESTED_DOORS].length > 1
          ) {
            for (const s of row[InternalColumns.SUGGESTED_DOORS].slice(
              1,
              exportConfig.suggestionAmount
            )) {
              const rowWithSuggestions = { ...baseRow }
              doorSuggestionKeys.forEach((key) => {
                addColumn(
                  `__suggestion__door__${key}`,
                  s[key],
                  rowWithSuggestions
                )
              })
              extraSuggestionRows.push(rowWithSuggestions)
            }
          }
          if (extraSuggestionRows.length > 0) {
            transformedRows.push(...extraSuggestionRows)
          }
        }

        // ### MATCHED COLUMNS ###
        // Add blank columns
        addColumn('__generated__firstbite_chain_id', '', baseRow)
        addColumn('__generated__firstbite_door_id', '', baseRow)
        addColumn(
          '__generated__chain_name',
          row[InternalColumns.MATCHED_CHAIN]?.name ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_domain',
          row[InternalColumns.MATCHED_CHAIN]?.domain ?? '',
          baseRow
        )
        addColumn(
          '__generated__chain_size',
          row[InternalColumns.MATCHED_CHAIN]?.count ?? '',
          baseRow
        )

        // Row is set - Add FB ID and Door ID
        if (row[InternalColumns.SET_CHAIN_PROXY_ID] !== -1) {
          // FB ID
          // Get Prefixed Firstbite ID e.g. RE12312
          let firstbiteId =
            (row[InternalColumns.MATCHED_CHAIN]?.type ?? '') +
            (row[InternalColumns.MATCHED_CHAIN]?.item_id ?? '')
          if (row[InternalColumns.IS_CUSTOM_COMPANY]) {
            firstbiteId = 'UG' + row[InternalColumns.SET_CHAIN_PROXY_ID]
          }
          addColumn('__generated__firstbite_chain_id', firstbiteId, baseRow)

          // Get Door ID e.g. 12312
          if (row[InternalColumns.MATCHED_DOOR]) {
            addColumn(
              '__generated__firstbite_door_id',
              row[InternalColumns.MATCHED_DOOR].item_id,
              baseRow
            )
          }
        }

        transformedRows.push(baseRow)
      }

      // Convert to CSV
      const csv = Papa.unparse(transformedRows)

      // Create and trigger download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', 'exported_data.csv')
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    [importData, exportConfig]
  )

  function handleFileUpload(file: File | null) {
    setRawFile(file)
    if (!file) return

    setIsUploading(true)
    Papa.parse<CSVRow>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      delimitersToGuess: [';', ','],
      complete: (results) => {
        const data = results.data.map((row) => {
          return {
            ...Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === 'string' ? value.slice(0, 200) : undefined,
              ])
            ),
          }
        }) as CSVRow[]
        const importData = {
          rows: data,
          headers:
            results.meta.fields?.map((h) => ({
              original: h,
              mapped: 'ignore',
              label: undefined,
            })) ?? [],
        }
        setImportData(importData)
        setShowHeaderMappingModal(true)
        setIsUploading(false)
      },
      error: (error) => {
        console.error('Error parsing CSV:', error)
        setIsUploading(false)
      },
    })
  }

  async function handleSetUnmatchedChains() {
    if (!importData) return
    if (!importNumber) return
    try {
      setIsSettingUnmatchedChains(true)
      await handleSave()
      await setUnmatchedChains(importNumber)
      refetchStatus()
    } catch (error) {
      console.error(error)
    } finally {
      setIsSettingUnmatchedChains(false)
    }
  }

  function loadImportPreviewData(data: ImportDataColumnar) {
    setImportData(fromColumnarJSON(data))
  }

  function setSubcategoryFilter(subcategoryId: number) {
    if (Number.isNaN(subcategoryId)) {
      clearSubcategoryFilter()
      return
    }

    setColumnFiltering([
      {
        id: InternalColumns.EXISTING_CHAIN_PROXY_SUBCATEGORY_ID,
        value: subcategoryId,
      },
    ])
  }

  function clearSubcategoryFilter() {
    setColumnFiltering([])
  }

  return {
    // Data
    rawFile,
    importPreview,
    importData,
    products,
    isLoading,
    isFetching,
    isLoadingStatus,

    // Modal states
    showHeaderMappingModal,
    setShowHeaderMappingModal,
    showSelectDealProductModal,
    selectedDealProduct,
    setSelectedDealProduct,
    showConfigureExportModal,
    setShowConfigureExportModal,

    // Loading states
    isSaving,
    isUploading,
    isSettingCompanies,
    isSettingTags,
    isSettingDeals,
    isSettingContacts,
    isSettingCompanyNotes,
    isSettingUnmatchedChains,

    // Form states
    hasPendingChanges,
    selectedTab,
    setSelectedTab,
    rowSelection,
    setRowSelection,
    validateModalData,
    importPreviewStatus,
    setShowSelectDealProductModal,
    showExportModal,
    setShowExportModal,

    // Computed values
    companySubcategories,
    mappedColumns,
    tableData,
    canAddTags,
    canAddDeals,
    canAddContacts,
    canAddCompanyNotes,
    wizardHeaders,
    otherOtherType,
    areAllRowsSet,
    allCompanies,
    companiesPrematched,
    amountCompaniesPrematched,
    companiesPendingReview,
    amountCompaniesPendingReview,
    companiesLeftUnmatched,
    amountCompaniesLeftUnmatched,
    importSummaryData,
    detailedImportData,
    importCounts,
    isAllImported,

    // Handlers
    updateMap,
    updateIsCustomCompany,
    updateCustomCompanyCategory,
    updateRowsReady,
    handleManualSearchSelect,
    handleSetCompanies,
    handleSetTags,
    handleSetDeals,
    handleSetContacts,
    handleSetCompanyNotes,
    handleValidation,
    handleSelectDealProduct,
    handleExport,
    handleFileUpload,
    handleSetUnmatchedChains,
    updateMatchedChain,
    updateMatchedDoor,
    handleSave,
    setValidateModalData,
    setSelectedRowId,
    selectedRowId,
    shouldShowBulkActions,
    selectedRowIds,
    chainMergerAvailableSubcategories,
    handleConfigureExport,
    updateMatchedChainFromCompanyHash,
    updateMatchedDoorFromCompanyHash,
    updateRowsReadyFromCompanyHash,
    updateIsCustomCompanyFromCompanyHash,
    updateCustomCompanyCategoryFromCompanyHash,

    // Column Filtering
    columnFiltering,
    setSubcategoryFilter,
    clearSubcategoryFilter,
    selectedSubcategoryFilter,

    // Export
    exportColumns,
    exportConfig,
    setExportConfig,

    // Review
    reviewing,
    setReviewing,
    handleReview,
  }
}

function toColumnarJSON(data: ImportData): ImportDataColumnar {
  if (!data.rows.length)
    return {
      headers: [],
      rows: {
        columns: [],
        rows: [],
      },
    }
  const columns = Object.keys(data.rows[0])
  const rows = data.rows.map((obj) => columns.map((key) => obj[key]))
  return {
    headers: data.headers,
    rows: {
      columns,
      rows,
    },
  }
}

function fromColumnarJSON(data: ImportDataColumnar): ImportData {
  return {
    headers: data.headers,
    rows:
      data.rows?.rows?.map(
        (row) =>
          Object.fromEntries(
            (data.rows?.columns || []).map((key, i) => [key, row[i]])
          ) as CSVRow
      ) || [],
  }
}

export const columnTypeColors: Record<ColumnType, ColumnColors> = {
  // Field that is used to match the company exactly, rendering other matching fields irrelevant
  primary_key: {
    background: '#E3F2FD',
    text: '#1E40AF',
  },
  // Field that is used to match the company
  company_match: {
    background: '#F3E5F5',
    text: '#6B21A8',
  },
  // Regular data
  tag_data: {
    background: '#ffffff',
    text: '#000000',
  },
  deal_data: {
    background: '#ffffff',
    text: '#000000',
  },
  contact_data: {
    background: '#ffffff',
    text: '#000000',
  },
  company_notes: {
    background: '#FFF8E1',
    text: '#F57F17',
  },
  // Ignore column
  ignore: {
    background: 'transparent',
    text: '#4B556340',
  },
  display_only: {
    background: '#ffffff',
    text: '#000000',
  },
}

export const columnOptions: Record<string, ColumnOption> = {
  firstbite_id: {
    name: 'First Bite Chain ID',
    type: 'primary_key',
  },
  door_id: {
    name: 'Door ID',
    type: 'primary_key',
  },
  company_name: {
    name: 'Company Name',
    type: 'company_match',
  },
  company_phone: {
    name: 'Company Phone',
    type: 'company_match',
  },
  company_opp_type: {
    name: 'Company Opp Type',
    type: 'company_match',
  },
  company_address_state: {
    name: 'Company State',
    type: 'company_match',
  },
  company_address_city: {
    name: 'Company City',
    type: 'company_match',
  },
  company_address_zip: {
    name: 'Company Zip',
    type: 'company_match',
  },
  company_address_country: {
    name: 'Company Country',
    type: 'company_match',
  },
  company_address_street: {
    name: 'Company Address',
    type: 'company_match',
  },
  company_address_street_2: {
    name: 'Company Address 2',
    type: 'company_match',
  },
  company_domain: {
    name: 'Company Domain',
    type: 'company_match',
  },
  company_full_address: {
    name: 'Company Full Address',
    type: 'company_match',
  },
  tag_name: {
    name: 'Tag Name',
    type: 'tag_data',
  },
  deal_sales_stage: {
    name: 'Deal Sales Stage',
    type: 'deal_data',
  },
  deal_account_owner: {
    name: 'Deal Account Owner Email',
    type: 'deal_data',
  },
  deal_product: {
    name: 'Deal Product',
    type: 'deal_data',
  },
  contact_full_name: {
    name: 'Contact Full Name',
    type: 'contact_data',
  },
  contact_first_name: {
    name: 'Contact First Name',
    type: 'contact_data',
  },
  contact_last_name: {
    name: 'Contact Last Name',
    type: 'contact_data',
  },
  contact_phone: {
    name: 'Contact Phone',
    type: 'contact_data',
  },
  contact_work_email: {
    name: 'Contact Work Email',
    type: 'contact_data',
  },
  contact_personal_email: {
    name: 'Contact Personal Email',
    type: 'contact_data',
  },
  contact_generic_email: {
    name: 'Contact Generic Email',
    type: 'contact_data',
  },
  contact_linkedin_url: {
    name: 'Contact LinkedIn URL',
    type: 'contact_data',
  },
  contact_title: {
    name: 'Contact Title',
    type: 'contact_data',
  },
  contact_label: {
    name: 'Contact Label',
    type: 'contact_data',
  },
  company_notes: {
    name: 'Company Notes',
    type: 'company_notes',
  },
}

const chainSuggestionKeys = [
  'name',
  'domain',
  'count',
  'item_id',
  'type',
  'score',
] as Array<keyof ChainSearchResult>

const doorSuggestionKeys = [
  'item_id',
  'name',
  'domain',
  'latitude',
  'longitude',
  'address',
  'city',
  'state',
  'country',
  'zipcode',
  'chain_id',
  'chain_name',
  'chain_size',
  'type',
  'score',
] as Array<keyof DoorSearchResult>

const generatedColumnKeys = [
  '__generated__firstbite_chain_id',
  '__generated__firstbite_door_id',
  '__generated__chain_name',
  '__generated__chain_domain',
  '__generated__chain_size',
]
